import { createFormatter } from 'pejer/src/helpers'
import type { Config, ConfigUrls } from 'pejer/src/types/base'
import type { PaymentVoucherDto } from 'pejer/src/types/PaymentVoucher'
import { NotificationResponse } from './Notification'
import { RefundRequestEmbed } from './tables/RefundRequests'
import { BusinessAgreement } from './types/Agreements'
import { ClubAccountHistory } from './types/ClubAccount'
import { ServerOptions } from './types/ServerOptions'

const urls = {
  connectedAccounts: {
    get: {
      path: '/api/embed/accounts/{id}',
      method: 'GET',
    },
    list: {
      path: '/api/embed/accounts',
      method: 'GET',
    },
    create: {
      path: '/api/embed/accounts',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
    upgradeToDirectRemittance: {
      path: '/api/embed/onboarding/{id}/DirectRemittance.pdf',
      method: 'GET',
    },
    getDirectRemittancePDF: {
      path: '/api/embed/onboarding/{id}/DirectRemittance.pdf',
      method: 'GET',
      formatter: createFormatter<string>(),
    },
    saveDirectRemittancePDF: {
      path: '/api/embed/onboarding/{id}/DirectRemittance.pdf',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
    confirmDirectRemittance: {
      path: '/api/embed/onboarding/{id}/confirmDirectRemittance',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
    archive: {
      path: '/api/embed/accounts/{id}',
      method: 'DELETE',
    },
  },
  payments: {
    get: {
      path: '/api/embed/payments/{id}',
      method: 'GET',
    },
    list: {
      path: '/api/embed/payments',
      method: 'GET',
    },
    refund: {
      path: '/api/embed/payments/{id}/refund',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
    'refund-partially': {
      path: '/api/embed/payments/{id}/refund/partially',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
    'club-account-register-payment': {
      path: '/api/embed/payments/{id}/club-account/register-payment',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
    'club-account-history': {
      path: '/api/embed/payments/{id}/club-account/history',
      method: 'GET',
      formatter: createFormatter<ClubAccountHistory>(),
    },
    'club-account-register-refund': {
      path: '/api/embed/payments/{id}/club-account/register-refund',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
  },
  refundRequests: {
    get: {
      path: '/api/embed/refunds/{id}',
      method: 'GET',
    },
    list: {
      path: '/api/embed/refunds/requests',
      method: 'GET',
      formatter: createFormatter<RefundRequestEmbed.ListItem[]>(),
    },
    accept: {
      path: '/api/embed/refunds/requests/{id}/accept',
      method: 'POST',
      formatter: createFormatter<RefundRequestEmbed.ListItem>(),
    },
  },
  invoices: {
    get: {
      path: '/api/embed/payments/{id}/invoice',
      method: 'GET',
    },
    list: {
      path: '/api/embed/payments/invoices',
      method: 'GET',
    },
    adjustOrRefund: {
      path: '/api/embed/payments/{id}/invoice/adjust',
      method: 'PUT',
      formatter: createFormatter<string>(),
    },
    sendReminder: {
      path: '/api/embed/payments/{id}/invoice/send-reminder',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
    startDebtCollection: {
      path: '/api/embed/payments/{id}/invoice/start-debt-collection',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
  },
  payouts: {
    get: {
      path: '/api/embed/payouts/{id}',
      method: 'GET',
    },
    list: {
      path: '/api/embed/payouts',
      method: 'GET',
    },
    getPaymentVoucher: {
      path: '/api/payouts/{id}/payment-voucher',
      method: 'GET',
      formatter: createFormatter<PaymentVoucherDto>(),
    },
  },
  personsOfInterest: {
    get: {
      path: '/api/embed/personsOfInterest/{id}',
      method: 'GET',
    },
    list: {
      path: '/api/embed/personsOfInterest',
      method: 'GET',
    },
    requireSignature: {
      path: '/api/embed/personsOfInterest/{id}/require-signature',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
    updateFromBrreg: {
      path: '/api/embed/businesses/{countryCode}/{organizationId}/update-from-brreg',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
  },
  onboarding: {
    getLink: {
      path: '/api/embed/onboarding/{id}/get-link',
      method: 'GET',
      formatter: createFormatter<string>(),
    },
    getProffData: {
      path: '/api/embed/onboarding/proffData',
      method: 'GET',
    },
    getBusinessInformation: {
      path: '/api/embed/business/{organizationID}/{countryCode}/information',
      method: 'GET',
    },
  },
  options: {
    get: {
      path: '/api/embed/options',
      method: 'GET',
      formatter: createFormatter<ServerOptions>(),
    },
  },
  agreements: {
    getBusinessAgreement: {
      path: '/embed/agreements/business/{countryCode}/{organizationId}/personalDebtCollection',
      method: 'GET',
      formatter: createFormatter<BusinessAgreement>(),
    },
    signBusinessAgreement: {
      path: '/embed/agreements/business/{countryCode}/{organizationId}/personalDebtCollection',
      method: 'POST',
      formatter: createFormatter<BusinessAgreement>(),
    },
  },
  businessExternalAccounts: {
    getAvailableBusinesses: {
      path: '/api/embed/business-external-accounts/available-businesses',
      method: 'GET',
      formatter: createFormatter<
        {
          organizationId: string
          countryCode: string
          name: string
          id: number
          bankIntegrationActivatedAt: Date
        }[]
      >(),
    },
    create: {
      path: '/api/embed/business-external-accounts',
      method: 'POST',
    },
    createAgreementPDF: {
      path: '/api/embed/business-external-accounts/agreement.pdf',
      method: 'POST',
      formatter: createFormatter<string>(),
    },
  },
  notifications: {
    get: {
      path: '/api/embed/notifications',
      method: 'GET',
      formatter: createFormatter<NotificationResponse>(),
    },
  },
} as const satisfies ConfigUrls

export const baseConfig = {
  cookieNames: {
    embedToken: 'x-embed-token',
  },
  urls,
} as const satisfies Partial<Config<typeof urls>>
